/**
 * The WebGPU renderer (architecture §3.9; art §3.1 frame graph; cheatsheet §3.10 r3 contract). One device, one
 * full-viewport canvas (premultiplied, GDD C130), one MSAA scene pass (figures → props → floor → hulls/keylines →
 * sky at z = 1 → particles) and one composite pass. Zero allocations in the frame loop (host mirrors are allocated once).
 *
 * Device-lost recovery (architecture §11): the GPU state is rebuilt in place from the retained CPU copies of every
 * mesh, look and pivot table, so handles stay valid; a second loss within 60 s reports `fatal` (→ lite mode).
 */
import tgpu, { type TgpuBindGroup, type TgpuRoot } from 'typegpu';
import type {
  ActorFrame, ActorGeom, Backdrop, CameraApi, CameraRig, CameraState, CreatureDesc, CreatureFx, CreatureHandle,
  CreatureLookData, ElementInk, GradePreset, PanelApi, PanelRect, PanelSpec, ParticleKind, PortraitView, PropHandle,
  PropMeshData, QualityTier, RenderStats, Renderer, RendererCaps, RendererDebugApi, RendererOptions, ScenePreset,
  Shot, ShotSubjects, SubPanel, Vec3, Viewport, VfxApi, WorldClocks,
} from '../shared';
import { FRAME_BREAK_CAP, MAX_BONES, MAX_CONTACTS, MAX_PROPS, MAX_SLOTS, MAX_SUBPANELS } from '../shared';
import { LOOK, LOOK_FLAG, LOOK_FLOATS, hexToLinear, TOKENS } from '../content/palette';
import { SMEAR } from '../content/fx/timescales';
import {
  BACKDROP_ID, BYTES, DEBUG_VIEW, FR, FRAME_ID, MAT_RAMP_COUNT, PARTICLE_FLOATS, PO, SL, creatureVertexLayout, frameLayout,
  hullVertexLayout, particleLayout, trailLayout, postLayout, propLayout, skinLayout, illustrationLayout, type DebugViewName,
} from './gpu/layouts';
import { DEPTH_FORMAT, allPipelines, buildPipelines, type PipelineSet } from './gpu/pipelines';
import { AutoTier, TIER_SETTINGS, deviceDefaultTier, internalSize, isTier, tierIndex, type TierSettings } from './quality';
import { LIGHTING, keyDir, rimLinear, writeMaterialRamps, type LightingId } from './lighting';
import { arenaFraming, cameraMatrices, newCameraMatrices, orbitCamera, projectPoint, type ArenaFraming, type Rect } from './camera';
import { EngineFeel } from './feel';
import { ParticlePool } from './particles/sim';
import {BladeTrails,TRAIL_CAP,TRAIL_FLOATS} from './particles/trails';
import { AtelierActorRenderer, type AtelierGpuActor } from './atelier/actors';
import type { AtelierActorAsset, AtelierActorFrame, AtelierActorHandle } from '../shared';

export class WebGpuUnavailableError extends Error {
  readonly reason: 'no-api' | 'no-adapter' | 'no-device' | 'pipeline';
  constructor(reason: 'no-api' | 'no-adapter' | 'no-device' | 'pipeline', message?: string) {
    super(message ?? `WebGPU unavailable: ${reason}`);
    this.name = 'WebGpuUnavailableError';
    this.reason = reason;
  }
}

export type DeviceEvent = { kind: 'lost'; message: string } | { kind: 'restored'; ms: number } | { kind: 'fatal'; message: string } | { kind: 'error'; message: string };

const BONE_FLOATS = MAX_BONES * 16;
const PANEL_REF_W = 358;

type PendingRead = { x: number; y: number; resolve: (v: [number, number, number]) => void };

interface MeshGpu { vb: GPUBuffer; vertexCount: number; hullVb: GPUBuffer | null; hullIb: GPUBuffer | null; hullIndexCount: number }

interface GpuState {
  root: TgpuRoot; device: GPUDevice; context: GPUCanvasContext; canvasFormat: GPUTextureFormat;
  settings: TierSettings; pipes: PipelineSet;
  buf: { frame: GPUBuffer; frames: GPUBuffer[]; light: GPUBuffer; mats: GPUBuffer; bones: GPUBuffer; lag: GPUBuffer; pivot: GPUBuffer; smear: GPUBuffer; looks: GPUBuffer; props: GPUBuffer; propInfo: GPUBuffer; post: GPUBuffer; particles: GPUBuffer; trails:GPUBuffer; readback: GPUBuffer | null };
  groups: { frame: TgpuBindGroup; frames: TgpuBindGroup[]; skin: TgpuBindGroup; prop: TgpuBindGroup; post: TgpuBindGroup | null };
  sampler: GPUSampler;
  targets: { w: number; h: number; msaaColor: GPUTexture | null; resolved: GPUTexture; held: GPUTexture; depth: GPUTexture } | null;
  meshes: Map<object, MeshGpu>;
  particleCap: number;
  lostAt: number;
  atelier: AtelierActorRenderer | null;
  illustration: {texture:GPUTexture; group:TgpuBindGroup} | null;
}
interface AtelierRecord {asset:AtelierActorAsset;gpu:AtelierGpuActor|null;frame:AtelierActorFrame|null;visible:boolean;disposed:boolean;generation:number}

const ZERO_GEOM = (): ActorGeom => ({
  slot: 0, min: [0, 0, 0], max: [0, 0, 0], forward: [1, 0, 0],
  parts: { eyes: [0, 0, 0], head: [0, 0, 0], chest: [0, 0, 0], full: [0, 0, 0], paws: [0, 0, 0], weapon: [0, 0, 0], tail: [0, 0, 0] },
});

class Creature implements CreatureHandle {
  boneCount: number;
  geom: ActorGeom = ZERO_GEOM();
  visible = true;
  mirrored = false;
  liftH = 0;
  readonly contacts = new Float32Array(8);
  readonly look = new ArrayBuffer(LOOK_FLOATS * 4);
  readonly lookF = new Float32Array(this.look);
  readonly lookU = new Uint32Array(this.look);
  fxQueue: Array<{ at: number; fx: Partial<CreatureFx> }> = [];
  outline = 1;
  keyline = false;
  hasFrame = false;
  readonly boneIdx: { head: number; neck: number; chest: number; pelvis: number; footL: number; footR: number; handR: number; tail: number; eyeL: number };
  constructor(private r: EngineRenderer, readonly slot: number, readonly desc: CreatureDesc, readonly meshKey: object) {
    this.boneCount = desc.rig.bones.length;
    new Uint8Array(this.look).set(new Uint8Array(desc.look, 0, Math.min(desc.look.byteLength, LOOK_FLOATS * 4)));
    const ix = desc.rig.index;
    this.boneIdx = {
      head: ix.head ?? 0, neck: ix.neck ?? 0, chest: ix.spine_02 ?? 0, pelvis: ix.pelvis ?? 0, footL: ix.foot_L ?? 0, footR: ix.foot_R ?? 0,
      handR: ix.hand_R ?? 0, tail: ix.tail_3 ?? ix.tail_2 ?? ix.tail_1 ?? 0, eyeL: ix.eye_L ?? ix.head ?? 0,
    };
    this.mirrored = !!desc.mirrored;
  }
  setFrame(frame: ActorFrame): void { this.r.setCreatureFrame(this, frame); }
  setFx(fx: Partial<CreatureFx>, atMs?: number): void { this.r.queueFx(this, fx, atMs); }
  setLook(look: CreatureLookData): void {
    const keep = this.lookF.slice(LOOK.line, LOOK.fxColor + 4);
    new Uint8Array(this.look).set(new Uint8Array(look, 0, Math.min(look.byteLength, LOOK_FLOATS * 4)));
    this.lookF.set(keep, LOOK.line);
    this.r.markLooks(); this.r.requestFrame();
  }
  setMirrored(on: boolean): void {
    this.mirrored = on;
    const u = this.lookU;
    u[LOOK.pattern + 2] = on ? (u[LOOK.pattern + 2] | LOOK_FLAG.mirrored) : (u[LOOK.pattern + 2] & ~LOOK_FLAG.mirrored);
    this.r.markLooks(); this.r.requestFrame();
  }
  setVisible(v: boolean): void { this.visible = v; this.r.requestFrame(); }
  dispose(): void { this.r.releaseCreature(this); }
}

class Prop implements PropHandle {
  visible = true;
  owner: Creature | null = null;
  readonly matrix = new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  det = 1;
  constructor(private r: EngineRenderer, readonly index: number, readonly mesh: PropMeshData, readonly meshKey: object) {}
  setMatrix(world: Float32Array): void {
    this.matrix.set(world);
    const m = world;
    this.det = m[0] * (m[5] * m[10] - m[6] * m[9]) - m[4] * (m[1] * m[10] - m[2] * m[9]) + m[8] * (m[1] * m[6] - m[2] * m[5]);
    this.r.markProps();
  }
  setLookFrom(owner: CreatureHandle | null): void { this.owner = owner as Creature | null; this.r.markProps(); }
  setFx(): void { /* props share the owner's look fx (flash, reveal, frame-break) */ }
  setVisible(v: boolean): void { this.visible = v; this.r.requestFrame(); }
  dispose(): void { this.r.releaseProp(this); }
}

const GRADES: Record<GradePreset, [number, number, number, number]> = {
  neutral: [1, 1, 1, 0], arena: [1, 1, 1, 0.03], dungeon: [0.92, 1.04, 1, 0.03], victory: [1.04, 1.01, 1.02, 0], danger: [1, 1.02, 1, 0.03],
};

export class EngineRenderer implements Renderer {
  quality: QualityTier;
  readonly stats: RenderStats = {
    fps: 0, cpuMs: 0, gpuMs: null, drawCalls: 0, triangles: 0, particles: 0, decals: 0, passes: 2, renderScale: 1, msaa: 4, textureMB: 0,
    slotsUsed: 0, propsUsed: 0, subPanels: 0, flashFramesLast1s: 0, frameBreakCount: 0, frameBreakSlots: [], contactsUsed: 0,
    smearSlots: 0, smearMaxH: 0, mirroredDraws: 0, transientWaits: 0, uploadBytes: 0,
  };
  /** Extra engine stats (`__marg.render.stats()` art §12 names). */
  readonly extra = { liveBands: 0, silhouettePx: 0, shakePx: 0, zoomPunch: 0, flashMsLast1s: 0, impactFramesShown: 0, background: 'vault', internalSize: [0, 0] as [number, number], p50Ms: 0, p90Ms: 0, frames: 0, deviceLosses: 0, stage: { hPx: 0, hRestPx: 0, hEngagedPx: 0, eyeLinePct: 0.6, groundLinePct: 0 } };
  readonly errors: string[] = [];

  private gpu: GpuState | null = null;
  private readonly canvas: HTMLCanvasElement;
  private reducedMotion: boolean;
  private flashes: boolean;
  private readonly frameBreakCap: number;
  private readonly autoTier: AutoTier | null;
  private listeners: Array<(e: DeviceEvent) => void> = [];

  // host mirrors (allocated once)
  private readonly frameHost = new Float32Array(FR.floats);
  private readonly lightHost = new Float32Array(SL.floats);
  private readonly lightU32 = new Uint32Array(this.lightHost.buffer);
  private readonly matsHost = new Float32Array(MAT_RAMP_COUNT * 12);
  private readonly bonesHost = new Float32Array(MAX_SLOTS * BONE_FLOATS);
  private readonly lagHost = new Float32Array(MAX_SLOTS * BONE_FLOATS);
  private readonly pivotHost = new Float32Array(MAX_SLOTS * MAX_BONES * 4);
  private readonly smearHost = new Float32Array(MAX_SLOTS * 4);
  private readonly looksHost = new Float32Array(MAX_SLOTS * LOOK_FLOATS);
  private readonly propsHost = new Float32Array(MAX_PROPS * 16);
  private readonly propInfoHost = new Uint32Array(MAX_PROPS * 4);
  private readonly postHost = new Float32Array(PO.floats);
  private particleHost = new Float32Array(1500 * PARTICLE_FLOATS);
  private readonly trails=new BladeTrails();
  private readonly trailHost=new Float32Array(TRAIL_CAP*TRAIL_FLOATS);
  private readonly lagDirty = new Uint8Array(MAX_SLOTS);
  private bonesLo = MAX_SLOTS;
  private bonesHi = -1;
  private looksDirty = true;
  private pivotDirty = true;
  private propsDirty = true;
  private matsDirty = true;

  // scene state
  private readonly creatures: Array<Creature | null> = new Array(MAX_SLOTS).fill(null);
  private readonly props: Array<Prop | null> = new Array(MAX_PROPS).fill(null);
  private scene: ScenePreset = { kind: 'none' };
  private lighting: LightingId = 'arena';
  private backdrop: Backdrop = 'vault';
  private grade: GradePreset = 'arena';
  private burn = 0;
  private panelSpec: PanelSpec | null = null;
  private vp: Viewport = { cssW: 390, cssH: 844, dpr: 1, portrait: true, safe: { t: 0, r: 0, b: 0, l: 0 } };
  private size = { canvasW: 1, canvasH: 1, sceneW: 1, sceneH: 1, pxPerCss: 1 };
  private camOverride: CameraState | null = null;
  /** Triptych sub-panels (art §7.3, architecture §5.9). */
  private sub: Array<{ rect01: [number, number, number, number]; hold: 'live' | 'freezeOnEnd'; cam: CameraState | null; frozen: boolean; copyPending: boolean }> = [];
  private subStartMs = 0;
  private subDividerMs = 120;
  private subDissolveMs = 150;
  private subEndAt: number | null = null;
  private readonly subCams = [newCameraMatrices(), newCameraMatrices(), newCameraMatrices()];
  private readonly subHosts = [new Float32Array(FR.floats), new Float32Array(FR.floats), new Float32Array(FR.floats)];
  private rig: CameraRig = { kind: 'arena', layout: 'portrait' };
  private framing: ArenaFraming | null = null;
  private engagedE: number | null = null;
  private trackOffsetX = 0;
  private readonly arenaCam: CameraState = { eye: [0, 1, 6], target: [0, 0.6, 0], fovYDeg: 20, dutchDeg: 0, orthoHalfH: 0, near: 0.1, far: 60 };
  private readonly cam = newCameraMatrices();
  private readonly projectCam = newCameraMatrices();
  private camState: CameraState = { eye: [0, 1, 6], target: [0, 0.6, 0], fovYDeg: 20, dutchDeg: 0, orthoHalfH: 0, near: 0.1, far: 60 };
  private readonly feel: EngineFeel;
  private readonly pool: ParticlePool;
  private pendingBursts: Array<{ kind: ParticleKind|null; at: Vec3; opts: Parameters<VfxApi['burst']>[2] }> = [];
  private clocks: WorldClocks = { presentMs: 0, worldMs: 0, vfxMs: 0 };
  private freezeT: number | null = null;
  private debugView: number = DEBUG_VIEW.none;
  private requested = true;
  private lastFrameAt = -1;
  private readonly dts = new Float32Array(120);
  private dtHead = 0;
  private pendingReads: PendingRead[] = [];
  private pendingCaptures: Array<(url: string) => void> = [];
  private meshKeys = new WeakMap<ArrayBuffer, object>();
  private destroyed = false;
  private atelierActors=new Set<AtelierRecord>();
  private qualityRevision=0;
  private illustrationSource:HTMLCanvasElement|null=null;

  constructor(opts: RendererOptions) {
    this.canvas = opts.canvas;
    this.reducedMotion = opts.reducedMotion;
    this.flashes = opts.flashes;
    this.frameBreakCap = Math.min(opts.frameBreakCap || FRAME_BREAK_CAP, FRAME_BREAK_CAP);
    const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    const def = deviceDefaultTier(coarse);
    this.quality = opts.quality === 'auto' ? def : opts.quality;
    this.autoTier = opts.quality === 'auto' ? new AutoTier(def, this.quality) : null;
    this.feel = new EngineFeel({ reducedMotion: this.reducedMotion, flashes: this.flashes, panelScale: () => this.bandScale() });
    this.pool = new ParticlePool(TIER_SETTINGS.ultra.particleCap);
    this.applyLighting('arena');
  }

  // ── boot / device ────────────────────────────────────────────────────────────────────────────────────────────────

  async init(): Promise<void> {
    this.gpu = await this.createGpu();
  }

  onDeviceEvent(fn: (e: DeviceEvent) => void): () => void {
    this.listeners.push(fn);
    return () => { this.listeners = this.listeners.filter((f) => f !== fn); };
  }
  private emit(e: DeviceEvent): void { for (const f of this.listeners) f(e); }

  private async createGpu(): Promise<GpuState> {
    if (typeof navigator === 'undefined' || !('gpu' in navigator)) throw new WebGpuUnavailableError('no-api');
    const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
    if (!adapter) throw new WebGpuUnavailableError('no-adapter');
    let device: GPUDevice;
    try { device = await adapter.requestDevice({ label: 'marg' }); } catch (e) { throw new WebGpuUnavailableError('no-device', String(e)); }
    const root = tgpu.initFromDevice({ device });
    device.addEventListener('uncapturederror', (e) => {
      const msg = (e as GPUUncapturedErrorEvent).error.message;
      this.errors.push(msg);
      if (this.errors.length > 50) this.errors.shift();
      console.error(`[engine] ${msg}`);
      this.emit({ kind: 'error', message: msg });
    });
    const lostAt = this.gpu?.lostAt ?? -1e9;
    void device.lost.then((info) => { if (info.reason !== 'destroyed' && !this.destroyed) void this.handleLost(info.message); });
    const context = this.canvas.getContext('webgpu');
    if (!context) throw new WebGpuUnavailableError('no-api', 'no webgpu canvas context');
    const canvasFormat = navigator.gpu.getPreferredCanvasFormat();
    context.configure({ device, format: canvasFormat, alphaMode: 'premultiplied', usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_SRC });
    const settings = TIER_SETTINGS[this.quality];
    const mk = (size: number, usage: number, label: string): GPUBuffer => device.createBuffer({ size: Math.ceil(size / 16) * 16, usage, label });
    const U = GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST, S = GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST;
    const buf = {
      frame: mk(BYTES.frame, U, 'frame'), frames: [0, 1, 2].map((i) => mk(BYTES.frame, U, 'frameBand' + i)), light: mk(BYTES.light, U, 'light'), mats: mk(BYTES.mats, U, 'mats'),
      bones: mk(BYTES.bones, S, 'bones'), lag: mk(BYTES.bones, S, 'bonesLag'), pivot: mk(BYTES.bindPivot, S, 'bindPivot'),
      smear: mk(BYTES.smear, U, 'smear'), looks: mk(BYTES.looks, U, 'looks'), props: mk(BYTES.props, S, 'props'),
      propInfo: mk(BYTES.propInfo, S, 'propInfo'), post: mk(BYTES.post, U, 'post'),
      particles: mk(TIER_SETTINGS.ultra.particleCap * BYTES.particle, GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST, 'particles'),
      trails:mk(TRAIL_CAP*TRAIL_FLOATS*4,GPUBufferUsage.VERTEX|GPUBufferUsage.COPY_DST,'blade-trails'),
      readback: null,
    };
    const groups = {
      frame: root.createBindGroup(frameLayout, { frame: buf.frame, light: buf.light, mats: buf.mats }),
      frames: [buf.frame, ...buf.frames].map((f) => root.createBindGroup(frameLayout, { frame: f, light: buf.light, mats: buf.mats })),
      skin: root.createBindGroup(skinLayout, { bones: buf.bones, bonesLag: buf.lag, bindPivot: buf.pivot, smear: buf.smear, looks: buf.looks }),
      prop: root.createBindGroup(propLayout, { props: buf.props, propInfo: buf.propInfo }),
      post: null,
    };
    const sampler = device.createSampler({ magFilter: 'linear', minFilter: 'linear', addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge' });
    device.pushErrorScope('validation');
    const pipes = buildPipelines(root, settings.msaa, settings.sceneFormat, canvasFormat);
    try {
      await Promise.all(allPipelines(pipes).map((p) => p.initAsync()));
    } catch (e) {
      throw new WebGpuUnavailableError('pipeline', String(e));
    }
    const err = await device.popErrorScope();
    if (err) throw new WebGpuUnavailableError('pipeline', err.message);
    const st: GpuState = { root, device, context, canvasFormat, settings, pipes, buf, groups, sampler, targets: null, meshes: new Map(), particleCap: settings.particleCap, lostAt, atelier:null, illustration:null };
    // re-upload everything the CPU retains (first boot: nothing; after a loss: every live creature and prop)
    for (const c of this.creatures) if (c) this.uploadMesh(st, c.meshKey, c.desc.mesh.vertices, c.desc.mesh.vertexCount, c.desc.mesh.hull);
    for (const p of this.props) if (p) this.uploadMesh(st, p.meshKey, p.mesh.vertices, p.mesh.vertexCount, p.mesh.hull);
    for(const actor of this.atelierActors)await this.mountAtelier(st,actor);
    // Actor textures may decode asynchronously during recovery; use the latest
    // page after those awaits, including a resize or release during that time.
    if(this.illustrationSource)this.uploadIllustration(st,this.illustrationSource);
    this.looksDirty = true; this.pivotDirty = true; this.propsDirty = true; this.matsDirty = true;
    this.bonesLo = 0; this.bonesHi = MAX_SLOTS - 1;
    this.lagDirty.fill(1);
    this.stats.msaa = settings.msaa;
    this.stats.renderScale = settings.renderScale;
    return st;
  }

  private async handleLost(message: string): Promise<void> {
    if(this.destroyed)return;
    const now = performance.now();
    const prev = this.gpu?.lostAt ?? -1e9;
    this.extra.deviceLosses++;
    this.emit({ kind: 'lost', message });
    this.gpu?.atelier?.dispose();
    for(const actor of this.atelierActors){actor.gpu=null;actor.generation++;}
    this.gpu = null;
    if (now - prev < 60_000) { this.emit({ kind: 'fatal', message: `second device loss within 60 s: ${message}` }); return; }
    try {
      const t0 = performance.now();
      const st = await this.createGpu();
      // Settings or the recovery timeout may dispose this renderer while
      // pipeline/actor uploads await. Never resurrect its abandoned device.
      if(this.destroyed){st.atelier?.dispose();st.illustration?.texture.destroy();st.device.destroy();return;}
      st.lostAt = now;
      this.gpu = st;
      this.requestFrame();
      this.emit({ kind: 'restored', ms: performance.now() - t0 });
    } catch (e) {
      if(!this.destroyed)this.emit({ kind: 'fatal', message: String(e) });
    }
  }

  /** Debug: simulate a device loss (architecture §11 recovery test). */
  async simulateDeviceLoss(): Promise<void> {
    const g = this.gpu;
    if (!g) return;
    await this.handleLost('simulated');
    g.device.destroy();
  }

  private uploadMesh(st: GpuState, key: object, vertices: ArrayBuffer, vertexCount: number, hull: CreatureDesc['mesh']['hull'] | null): MeshGpu {
    const have = st.meshes.get(key);
    if (have) return have;
    const d = st.device;
    const vb = d.createBuffer({ size: Math.max(16, Math.ceil(vertices.byteLength / 4) * 4), usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST, label: 'mesh' });
    d.queue.writeBuffer(vb, 0, vertices, 0, Math.floor(vertices.byteLength / 4) * 4);
    let hullVb: GPUBuffer | null = null, hullIb: GPUBuffer | null = null, hullIndexCount = 0;
    if (hull && hull.indexCount > 0) {
      hullVb = d.createBuffer({ size: Math.max(16, Math.ceil(hull.vertices.byteLength / 4) * 4), usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST, label: 'hull' });
      d.queue.writeBuffer(hullVb, 0, hull.vertices, 0, Math.floor(hull.vertices.byteLength / 4) * 4);
      const n = hull.indexCount + (hull.indexCount & 1);
      const idx = new Uint16Array(n);
      idx.set(hull.indices.subarray(0, hull.indexCount));
      hullIb = d.createBuffer({ size: Math.max(16, n * 2), usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST, label: 'hullIdx' });
      d.queue.writeBuffer(hullIb, 0, idx);
      hullIndexCount = hull.indexCount;
    }
    const m: MeshGpu = { vb, vertexCount, hullVb, hullIb, hullIndexCount };
    st.meshes.set(key, m);
    return m;
  }

  private meshKeyFor(buf: ArrayBuffer): object {
    let k = this.meshKeys.get(buf);
    if (!k) { k = {}; this.meshKeys.set(buf, k); }
    return k;
  }

  private async mountAtelier(st:GpuState,actor:AtelierRecord):Promise<void> {
    const generation=++actor.generation;
    st.atelier??=new AtelierActorRenderer(st.root,st.settings.sceneFormat,st.settings.msaa,DEPTH_FORMAT);
    const gpu=await st.atelier.add(actor.asset);
    if(actor.disposed||actor.generation!==generation||this.destroyed){gpu.dispose();return;}
    actor.gpu?.dispose();actor.gpu=gpu;
  }
  async addAtelierActor(asset:AtelierActorAsset):Promise<AtelierActorHandle|null> {
    if(!this.gpu||this.destroyed||this.atelierActors.size+this.creatures.filter(Boolean).length>=MAX_SLOTS)return null;
    const actor:AtelierRecord={asset,gpu:null,frame:null,visible:true,disposed:false,generation:0};
    this.atelierActors.add(actor);
    const dispose=()=>{if(actor.disposed)return;actor.disposed=true;actor.generation++;actor.gpu?.dispose();actor.gpu=null;this.atelierActors.delete(actor);this.stats.slotsUsed=this.creatures.filter(Boolean).length+this.atelierActors.size;this.requestFrame();};
    try{await this.mountAtelier(this.gpu,actor);}catch(error){dispose();throw error;}
    if(actor.disposed)return null;
    this.stats.slotsUsed=this.creatures.filter(Boolean).length+this.atelierActors.size;
    return {setFrame:frame=>{if(!actor.disposed){actor.frame=frame;this.requestFrame();}},setVisible:visible=>{actor.visible=visible;this.requestFrame();},dispose};
  }

  // ── quality ──────────────────────────────────────────────────────────────────────────────────────────────────────

  get settings(): TierSettings { return TIER_SETTINGS[this.quality]; }

  async setQuality(t: QualityTier): Promise<void> {
    const st=this.gpu;if(!isTier(t)||!st)return;
    const revision=++this.qualityRevision,ns=TIER_SETTINGS[t],old=st.settings;
    if(ns.msaa!==old.msaa||ns.sceneFormat!==old.sceneFormat) {
      const pipes=buildPipelines(st.root,ns.msaa,ns.sceneFormat,st.canvasFormat);
      await Promise.all(allPipelines(pipes).map(p=>p.initAsync()));
      if(this.gpu!==st||this.destroyed||revision!==this.qualityRevision)return;
      const next=this.atelierActors.size?new AtelierActorRenderer(st.root,ns.sceneFormat,ns.msaa,DEPTH_FORMAT):null;
      const prepared=new Map<AtelierRecord,AtelierGpuActor>();
      try {
        if(next)for(const actor of this.atelierActors) {
          const gpu=await next.add(actor.asset);
          if(actor.disposed)gpu.dispose();else prepared.set(actor,gpu);
        }
        if(this.gpu!==st||this.destroyed||revision!==this.qualityRevision) {
          for(const gpu of prepared.values())gpu.dispose();next?.dispose();return;
        }
      }catch(error){for(const gpu of prepared.values())gpu.dispose();next?.dispose();throw error;}
      // Commit the format, targets and all actor resources together. The previous
      // scene stays drawable throughout asynchronous mip/pipeline preparation.
      const previous=st.atelier;
      for(const [actor,gpu] of prepared){if(actor.disposed){gpu.dispose();continue;}actor.generation++;actor.gpu?.dispose();actor.gpu=gpu;}
      st.atelier=next;st.pipes=pipes;previous?.dispose();this.destroyTargets();
    }
    if(this.gpu!==st||this.destroyed||revision!==this.qualityRevision)return;
    this.quality=t;st.settings=ns;this.stats.msaa=ns.msaa;this.stats.renderScale=ns.renderScale;this.resize(this.vp);
  }

  caps(): Readonly<RendererCaps> {
    const s = this.settings;
    return { triptych: this.atelierActors.size===0, smear: s.smear && !this.reducedMotion && this.atelierActors.size===0, msaa: s.msaa, normalId: false };
  }

  // ── scene ────────────────────────────────────────────────────────────────────────────────────────────────────────

  private uploadIllustration(st:GpuState,source:HTMLCanvasElement):void{
    const texture=st.device.createTexture({label:'procedural-manuscript',size:[source.width,source.height],format:'rgba8unorm-srgb',usage:GPUTextureUsage.TEXTURE_BINDING|GPUTextureUsage.COPY_DST|GPUTextureUsage.RENDER_ATTACHMENT});
    st.device.queue.copyExternalImageToTexture({source},{texture},{width:source.width,height:source.height});
    const group=st.root.createBindGroup(illustrationLayout,{page:texture.createView(),samp:st.sampler});
    const previous=st.illustration?.texture;
    this.stats.textureMB+=(source.width*source.height-(previous?previous.width*previous.height:0))*4/(1024*1024);
    previous?.destroy();st.illustration={texture,group};
  }
  setIllustration(source:HTMLCanvasElement|null):()=>void{
    this.illustrationSource=source;
    if(this.gpu){
      if(source)this.uploadIllustration(this.gpu,source);
      else{const t=this.gpu.illustration?.texture;if(t){this.stats.textureMB-=t.width*t.height*4/(1024*1024);t.destroy();}this.gpu.illustration=null;}
    }
    this.requestFrame();
    return ()=>{if(source&&this.illustrationSource===source)this.setIllustration(null);};
  }

  setScene(p: ScenePreset): void {
    this.scene = p;
    if (p.kind === 'arena') {
      this.setBackdropNow(p.backdrop);
      this.applyLighting(p.lighting === 'crypt' ? 'crypt' : p.backdrop === 'gold' ? 'goldGround' : p.backdrop === 'diaper' || p.backdrop === 'quatrefoil' ? 'diaper' : 'arena');
      this.grade = p.lighting === 'crypt' ? 'dungeon' : 'arena';
    } else if (p.kind === 'stage') {
      this.setBackdropNow(p.backdrop);
      this.applyLighting(p.backdrop === 'fresco' ? 'constructor' : 'arena');
      this.grade = 'neutral';
    } else if (p.kind === 'cardTable') {
      this.setBackdropNow('vellum'); this.applyLighting('cardTable'); this.grade = 'neutral';
    } else if (p.kind === 'folioMap') {
      this.setBackdropNow('vellum'); this.applyLighting('folio'); this.grade = 'arena';
    } else if (p.kind === 'expedition') {
      this.setBackdropNow('vellum');const lighting=p.biome===11?'crypt':'constructor';if(this.lighting!==lighting)this.applyLighting(lighting);this.grade='neutral';
    }
    this.requestFrame();
  }

  private setBackdropNow(bg: Backdrop): void {
    this.backdrop = bg;
    this.extra.background = bg;
  }

  private applyLighting(id: LightingId): void {
    this.lighting = id;
    const p = LIGHTING[id];
    const L = keyDir(p);
    const h = this.lightHost;
    h[SL.keyDir] = L[0]; h[SL.keyDir + 1] = L[1]; h[SL.keyDir + 2] = L[2]; h[SL.keyDir + 3] = p.T1;
    const kt = hexToLinear('#FFF3DC');
    h[SL.keyTint] = kt[0]; h[SL.keyTint + 1] = kt[1]; h[SL.keyTint + 2] = kt[2]; h[SL.keyTint + 3] = p.T2;
    const tint = hexToLinear(TOKENS.lapis);
    h[SL.shadowTint] = tint[0]; h[SL.shadowTint + 1] = tint[1]; h[SL.shadowTint + 2] = tint[2]; h[SL.shadowTint + 3] = p.hatchStrength;
    const rim = rimLinear(p);
    h[SL.rim] = rim[0]; h[SL.rim + 1] = rim[1]; h[SL.rim + 2] = rim[2]; h[SL.rim + 3] = p.rim ? p.rim.strength : 0;
    h[SL.hatch] = p.rim ? p.rim.threshold : 1;
    writeMaterialRamps(this.matsHost, p);
    this.matsDirty = true;
  }

  // ── creatures and props ──────────────────────────────────────────────────────────────────────────────────────────

  addCreature(desc: CreatureDesc): CreatureHandle | null {
    if(this.atelierActors.size+this.creatures.filter(Boolean).length>=MAX_SLOTS)return null;
    const slot = this.pickSlot(desc);
    if (slot < 0) return null;
    const meshKey = desc.shareMeshWith ? (desc.shareMeshWith as Creature).meshKey : this.meshKeyFor(desc.mesh.vertices);
    const c = new Creature(this, slot, desc, meshKey);
    this.creatures[slot] = c;
    if (this.gpu) this.uploadMesh(this.gpu, meshKey, desc.mesh.vertices, desc.mesh.vertexCount, desc.mesh.hull);
    // bind pivots (pivot.xyz, 1) — once per addCreature
    const at = slot * MAX_BONES * 4;
    for (let b = 0; b < MAX_BONES; b++) {
      const bone = desc.rig.bones[b];
      this.pivotHost[at + b * 4] = bone ? bone.pivot[0] : 0;
      this.pivotHost[at + b * 4 + 1] = bone ? bone.pivot[1] : 0;
      this.pivotHost[at + b * 4 + 2] = bone ? bone.pivot[2] : 0;
      this.pivotHost[at + b * 4 + 3] = 1;
    }
    // identity palette until the first setFrame
    for (let b = 0; b < MAX_BONES; b++) {
      const o = slot * BONE_FLOATS + b * 16;
      this.bonesHost.fill(0, o, o + 16);
      this.bonesHost[o] = 1; this.bonesHost[o + 5] = 1; this.bonesHost[o + 10] = 1; this.bonesHost[o + 15] = 1;
    }
    this.bonesLo = Math.min(this.bonesLo, slot); this.bonesHi = Math.max(this.bonesHi, slot);
    this.smearHost.set([0, SMEAR.lagMs / 1000, SMEAR.capH, SMEAR.thresholdHps], slot * 4);
    c.keyline = LIGHTING[this.lighting].keyline;
    this.pivotDirty = true; this.looksDirty = true;
    this.stats.slotsUsed = this.creatures.filter(Boolean).length + this.atelierActors.size;
    this.requestFrame();
    return c;
  }

  private pickSlot(desc: CreatureDesc): number {
    const free = (s: number): boolean => this.creatures[s] === null;
    if (desc.role === 'main') { if (free(0)) return 0; if (free(1)) return 1; }
    if (desc.role === 'boss' && free(1)) return 1;
    if (desc.role === 'pet') for (let s = 2; s <= 9; s++) if (free(s)) return s;
    if (desc.role === 'transient') { for (let s = 10; s <= 11; s++) if (free(s)) return s; for (let s = 2; s <= 9; s++) if (free(s)) return s; this.stats.transientWaits++; return -1; }
    for (let s = 0; s < MAX_SLOTS; s++) if (free(s)) return s;
    return -1;
  }

  releaseCreature(c: Creature): void {
    if (this.creatures[c.slot] !== c) return;
    this.creatures[c.slot] = null;
    const shared = this.creatures.some((o) => o && o.meshKey === c.meshKey) || this.props.some((p) => p && p.meshKey === c.meshKey);
    if (!shared && this.gpu) {
      const m = this.gpu.meshes.get(c.meshKey);
      if (m) { m.vb.destroy(); m.hullVb?.destroy(); m.hullIb?.destroy(); this.gpu.meshes.delete(c.meshKey); }
    }
    this.stats.slotsUsed = this.creatures.filter(Boolean).length + this.atelierActors.size;
    this.requestFrame();
  }

  addProp(mesh: PropMeshData): PropHandle | null {
    const i = this.props.indexOf(null);
    if (i < 0) return null;
    const key = this.meshKeyFor(mesh.vertices);
    const p = new Prop(this, i, mesh, key);
    this.props[i] = p;
    if (this.gpu) this.uploadMesh(this.gpu, key, mesh.vertices, mesh.vertexCount, mesh.hull);
    this.stats.propsUsed = this.props.filter(Boolean).length;
    this.propsDirty = true;
    this.requestFrame();
    return p;
  }

  releaseProp(p: Prop): void {
    if (this.props[p.index] !== p) return;
    this.props[p.index] = null;
    const shared = this.props.some((o) => o && o.meshKey === p.meshKey) || this.creatures.some((c) => c && c.meshKey === p.meshKey);
    if (!shared && this.gpu) {
      const m = this.gpu.meshes.get(p.meshKey);
      if (m) { m.vb.destroy(); m.hullVb?.destroy(); m.hullIb?.destroy(); this.gpu.meshes.delete(p.meshKey); }
    }
    this.stats.propsUsed = this.props.filter(Boolean).length;
    this.requestFrame();
  }

  markLooks(): void { this.looksDirty = true; }
  markProps(): void { this.propsDirty = true; this.requested = true; }

  setCreatureFrame(c: Creature, f: ActorFrame): void {
    const o = c.slot * BONE_FLOATS;
    const n = Math.min(f.boneCount, MAX_BONES) * 16;
    if (f.palette.length === n) this.bonesHost.set(f.palette, o);
    else for (let i = 0; i < n; i++) this.bonesHost[o + i] = f.palette[i];
    if (c.liftH !== 0) for (let b = 0; b < f.boneCount; b++) this.bonesHost[o + b * 16 + 13] += c.liftH;
    const k = this.settings.smear && !this.reducedMotion ? f.smearK : 0;
    this.smearHost[c.slot * 4] = k;
    if (k > 0) {
      if (f.paletteLag.length === n) this.lagHost.set(f.paletteLag, o);
      else for (let i = 0; i < n; i++) this.lagHost[o + i] = f.paletteLag[i];
      this.lagDirty[c.slot] = 1;
    }
    for (let i = 0; i < 8 && i < f.contacts.length; i++) c.contacts[i] = f.contacts[i];
    if (c.liftH !== 0) { c.contacts[2] += c.liftH; c.contacts[6] += c.liftH; }
    for (let i = 0; i < 4 && i < f.face.length; i++) c.lookF[LOOK.face + i] = f.face[i];
    this.updateGeom(c);
    c.hasFrame = true;
    this.bonesLo = Math.min(this.bonesLo, c.slot); this.bonesHi = Math.max(this.bonesHi, c.slot);
    this.looksDirty = true;
    this.requested = true;
  }

  private updateGeom(c: Creature): void {
    const g = c.geom;
    g.slot = c.slot;
    const o = c.slot * BONE_FLOATS, po = c.slot * MAX_BONES * 4;
    let minX = 1e9, minY = 1e9, minZ = 1e9, maxX = -1e9, maxY = -1e9, maxZ = -1e9;
    const origin = (b: number, out: Vec3): Vec3 => {
      const m = o + b * 16, px = this.pivotHost[po + b * 4], py = this.pivotHost[po + b * 4 + 1], pz = this.pivotHost[po + b * 4 + 2];
      const B = this.bonesHost;
      out[0] = B[m] * px + B[m + 4] * py + B[m + 8] * pz + B[m + 12];
      out[1] = B[m + 1] * px + B[m + 5] * py + B[m + 9] * pz + B[m + 13];
      out[2] = B[m + 2] * px + B[m + 6] * py + B[m + 10] * pz + B[m + 14];
      return out;
    };
    const tmp: Vec3 = g.parts.full;
    for (let b = 0; b < c.boneCount; b++) {
      origin(b, tmp);
      if (tmp[0] < minX) minX = tmp[0]; if (tmp[1] < minY) minY = tmp[1]; if (tmp[2] < minZ) minZ = tmp[2];
      if (tmp[0] > maxX) maxX = tmp[0]; if (tmp[1] > maxY) maxY = tmp[1]; if (tmp[2] > maxZ) maxZ = tmp[2];
    }
    g.min[0] = minX; g.min[1] = Math.min(minY, 0); g.min[2] = minZ;
    g.max[0] = maxX; g.max[1] = maxY + 0.16; g.max[2] = maxZ;
    origin(c.boneIdx.head, g.parts.head);
    origin(c.boneIdx.eyeL, g.parts.eyes);
    origin(c.boneIdx.chest, g.parts.chest);
    origin(c.boneIdx.footL, g.parts.paws);
    origin(c.boneIdx.handR, g.parts.weapon);
    origin(c.boneIdx.tail, g.parts.tail);
    g.parts.full[0] = (minX + maxX) / 2; g.parts.full[1] = (Math.min(minY, 0) + maxY) / 2; g.parts.full[2] = (minZ + maxZ) / 2;
    g.forward[0] = c.mirrored ? -1 : 1;
  }

  queueFx(c: Creature, fx: Partial<CreatureFx>, atMs?: number): void {
    const at = atMs ?? this.clocks.presentMs;
    if (at <= this.clocks.presentMs) this.applyFx(c, fx);
    else c.fxQueue.push({ at, fx });
    this.requestFrame();
  }

  private applyFx(c: Creature, fx: Partial<CreatureFx>): void {
    const f = c.lookF;
    const u = c.lookU;
    if (fx.hitFlash !== undefined) f[LOOK.fx] = fx.hitFlash;
    if (fx.paintProgress !== undefined) f[LOOK.fx + 1] = fx.paintProgress;
    if (fx.wearLevel !== undefined) f[LOOK.fx + 2] = fx.wearLevel;
    if (fx.frameBreak !== undefined) {
      const breaking = this.creatures.filter((o) => o && o !== c && o.lookF[LOOK.fx + 3] > 0.5).length;
      if (fx.frameBreak === 1 && breaking >= this.frameBreakCap) console.warn(`[engine] frame-break refused for slot ${c.slot}: cap ${this.frameBreakCap}`);
      else f[LOOK.fx + 3] = fx.frameBreak;
    }
    if (fx.fxColor) f.set(fx.fxColor, LOOK.fxColor);
    if (fx.status !== undefined) u[LOOK.pattern + 3] = fx.status;
    if (fx.outline !== undefined) c.outline = fx.outline;
    if (fx.keyline !== undefined) {
      c.keyline = fx.keyline;
      u[LOOK.pattern + 2] = fx.keyline ? u[LOOK.pattern + 2] | LOOK_FLAG.keyline : u[LOOK.pattern + 2] & ~LOOK_FLAG.keyline;
    }
    this.looksDirty = true;
  }

  // ── APIs: camera, panel, vfx, debug ──────────────────────────────────────────────────────────────────────────────

  readonly camera: CameraApi = this.makeCameraApi();

  private makeCameraApi(): CameraApi {
    const r = this;
    return {
    setRig: (rig: CameraRig) => { this.rig = rig; this.framing = null; this.requestFrame(); },
    playShots: (shots: readonly Shot[], subjects: ShotSubjects) => { void shots; void subjects; return 0; },
    stopShots: () => undefined,
    setSubPanels: (panels: readonly SubPanel[] | null, o?: { drawDividersMs?: number; dissolveMs?: number }) => r.setSubPanels(panels, o),
    hold: () => undefined,
    punch: (pct, o) => { this.feel.punch(pct, o?.atMs ?? this.clocks.presentMs, o?.inMs, o?.holdMs, o?.outMs); this.requestFrame(); },
    set: (state: CameraState | null) => { this.camOverride = state ? { ...state, eye: [...state.eye], target: [...state.target] } : null; this.requestFrame(); },
    get state() { return r.camState; },
    framingReport: () => [],
    };
  }

  readonly panel: PanelApi = {
    set: (spec: PanelSpec) => { this.panelSpec = { ...spec, rect: { ...spec.rect } }; this.framing = null; this.requestFrame(); },
    kick: (amp, atMs) => { this.feel.kick(amp, atMs ?? this.clocks.presentMs); this.requestFrame(); },
    impact: (kind, atMs) => { const ok = this.feel.impact(kind, atMs ?? this.clocks.presentMs); this.requestFrame(); return ok; },
    setBurn: (b) => { this.burn = b; this.requestFrame(); },
    setGrade: (g) => { this.grade = g; this.requestFrame(); },
    setBackdrop: (bg) => { this.setBackdropNow(bg); this.requestFrame(); },
    inkWipe: (ms) => new Promise<void>((res) => setTimeout(res, this.reducedMotion ? 150 : (ms ?? 700))),
  };

  readonly vfx: VfxApi = {
    burst: (kind, at, opts) => { this.pendingBursts.push({ kind, at: [at[0], at[1], at[2]], opts }); this.requestFrame(); },
    trail:(key,base,tip,opts)=>{if(!this.reducedMotion)this.trails.sample(key,base,tip,opts);this.requestFrame();},
    clearTrail:(key)=>{this.trails.clear(key);this.requestFrame();},
    flareCuts:(atMs)=>{this.pendingBursts.push({kind:null,at:[0,0,0],opts:{seed:0,atMs}});this.requestFrame();},
    decal: () => { this.stats.decals = Math.min(this.stats.decals + 1, this.settings.decalCap); },
    rays: () => undefined,
    clear: () => { this.pool.clear(); this.trails.clear(); this.pendingBursts.length = 0; },
  };

  readonly debug: RendererDebugApi = {
    setView: (name: string | null) => { this.debugView = name && name in DEBUG_VIEW ? DEBUG_VIEW[name as DebugViewName] : DEBUG_VIEW.none; this.requestFrame(); },
    samplePixel: (x, y) => new Promise((resolve) => { this.pendingReads.push({ x, y, resolve }); this.requestFrame(); }),
    capture: () => new Promise((resolve) => { this.pendingCaptures.push(resolve); this.requestFrame(); }),
    bounds: () => this.creatures.filter((c): c is Creature => !!c && c.visible).map((c) => ({ slot: c.slot, rect: this.screenRect(c) })),
    lift: (slot, h) => { const c = this.creatures[slot]; if (c) { c.liftH = h; this.requestFrame(); } },
    fillSlots: (n) => {
      for (let i = 0; i < n; i++) {
        const any = this.creatures.find(Boolean);
        if (!any) return;
        const h = this.addCreature({ ...any.desc, role: 'transient', shareMeshWith: any });
        h?.setVisible(false);
      }
    },
  };

  /** Art §7.3 triptych bands (≤ MAX_SUBPANELS); null dissolves them away over `dissolveMs`. */
  setSubPanels(panels: readonly SubPanel[] | null, o?: { drawDividersMs?: number; dissolveMs?: number }): void {
    const now = this.clocks.presentMs;
    this.subDividerMs = o?.drawDividersMs ?? 120;
    this.subDissolveMs = o?.dissolveMs ?? 150;
    if (panels && panels.length) {
      this.sub = panels.slice(0, MAX_SUBPANELS).map((p) => ({ rect01: [...p.rect01] as [number, number, number, number], hold: p.hold, cam: null, frozen: false, copyPending: false }));
      this.subStartMs = now;
      this.subEndAt = null;
    } else if (this.sub.length) {
      this.subEndAt = now;
    }
    this.stats.subPanels = this.sub.length;
    this.requestFrame();
  }
  /** Camera of one band (shots routed to `panel`); null = the main camera. */
  setSubPanelCamera(i: number, cam: CameraState | null): void {
    const b = this.sub[i];
    if (b) { b.cam = cam ? { ...cam, eye: [...cam.eye], target: [...cam.target] } : null; this.requestFrame(); }
  }
  /** The band's shot ended: a `freezeOnEnd` band keeps its last pixels (one copy into the held texture). */
  endSubPanelShot(i: number): void {
    const b = this.sub[i];
    if (b && b.hold === 'freezeOnEnd' && !b.frozen) { b.frozen = true; b.copyPending = true; this.requestFrame(); }
  }

  /** Freeze shader time (art `freeze=`): deterministic screenshots. null = live. */
  freeze(t: number | null): void { this.freezeT = t; this.requestFrame(); }
  setReducedMotion(on: boolean): void { this.reducedMotion = on; if(on)this.trails.clear(); this.feel.setOptions({ reducedMotion: on, flashes: this.flashes }); }
  setFlashes(on: boolean): void { this.flashes = on; this.feel.setOptions({ reducedMotion: this.reducedMotion, flashes: on }); }
  /** Engaged framing (art §7.2): E in H, or null for the rest framing. */
  setEngaged(E: number | null): void { this.engagedE = E; this.framing = null; this.requestFrame(); }
  /** Lab/key-frame composition nudge of the arena x-track (H). */
  setTrackOffset(x: number): void { this.trackOffsetX = x; this.requestFrame(); }
  get framingInfo(): ArenaFraming | null { return this.framing; }

  // ── frame lifecycle ──────────────────────────────────────────────────────────────────────────────────────────────

  advanceTo(clocks: Readonly<WorldClocks>): void {
    const previous=this.clocks;
    this.clocks = { presentMs: clocks.presentMs, worldMs: clocks.worldMs, vfxMs: clocks.vfxMs };
    for (const c of this.creatures) {
      if (!c || c.fxQueue.length === 0) continue;
      c.fxQueue.sort((a, b) => a.at - b.at);
      while (c.fxQueue.length && c.fxQueue[0].at <= clocks.presentMs) this.applyFx(c, c.fxQueue.shift()!.fx);
    }
    this.pendingBursts.sort((a,b)=>(a.opts.atMs??previous.presentMs)-(b.opts.atMs??previous.presentMs));
    while(this.pendingBursts.length&&(this.pendingBursts[0].opts.atMs??previous.presentMs)<=clocks.presentMs) {
      const b=this.pendingBursts.shift()!,at=b.opts.atMs??previous.presentMs;
      const interval=clocks.presentMs-previous.presentMs;
      const fraction=interval>0?Math.max(0,Math.min(1,(at-previous.presentMs)/interval)):1;
      this.pool.advanceTo(previous.vfxMs+(clocks.vfxMs-previous.vfxMs)*fraction);
      if(b.kind===null){this.pool.flareCuts();continue;}
      const cap=this.settings.particleCap,n=b.opts.count;
      this.pool.burst(b.kind,b.at,{seed:b.opts.seed,dir:b.opts.dir,count:n!==undefined&&this.quality==='low'?Math.ceil(n*.5):n,element:b.opts.element,scale:b.opts.scale,rotation:b.opts.rotation,lifeMs:b.opts.lifeMs});
      if(this.pool.count>cap)this.pool.count=cap;
    }
    this.pool.advanceTo(clocks.vfxMs);
    this.trails.prune(clocks.presentMs);
  }

  isAnimating(): boolean {
    if (this.scene.kind === 'none') return this.pool.count > 0||this.trails.size>0;
    if (this.pool.count > 0 || this.feel.isActive(this.clocks.presentMs)) return true;
    if (this.sub.length && (this.subEndAt !== null || this.clocks.presentMs - this.subStartMs < this.subDividerMs + 50)) return true;
    return !this.reducedMotion && this.freezeT === null;
  }
  requestFrame(): void { this.requested = true; }
  get frameRequested(): boolean { return this.requested; }

  resize(vp: Viewport): void {
    this.vp = { ...vp, safe: { ...vp.safe } };
    const s = internalSize(vp.cssW, vp.cssH, vp.dpr, this.settings);
    this.size = s;
    if (this.canvas.width !== s.canvasW || this.canvas.height !== s.canvasH) { this.canvas.width = s.canvasW; this.canvas.height = s.canvasH; }
    this.extra.internalSize = [s.sceneW, s.sceneH];
    this.framing = null;
    this.requestFrame();
  }

  private bandScale(): number {
    const w = this.panelSpec ? this.panelSpec.rect.w : PANEL_REF_W;
    return Math.min(1.75, Math.max(0.9, w / PANEL_REF_W));
  }

  /** Panel and picture rects in CSS px (canvas space). */
  private rects(): { panel: Rect; picture: Rect; band: number } {
    const spec = this.panelSpec;
    const panel: Rect = spec ? spec.rect : { x: 0, y: 0, w: this.vp.cssW, h: this.vp.cssH };
    const band = spec && spec.frame !== 'none' ? 16 * this.bandScale() : 0;
    return { panel, picture: { x: panel.x + band, y: panel.y + band, w: panel.w - 2 * band, h: panel.h - 2 * band }, band };
  }

  private updateCamera(): void {
    const { picture } = this.rects();
    if (this.camOverride) { this.camState = this.camOverride; return; }
    if (this.rig.kind === 'fixed') { this.camState = this.rig.state; return; }
    if (this.rig.kind === 'arena') {
      if (!this.framing) {
        this.framing = arenaFraming(picture, this.bandScale(), { midX: 0, engagedE: this.engagedE });
        this.extra.stage = { hPx: this.framing.hPx, hRestPx: this.framing.hRestPx, hEngagedPx: this.framing.hEngagedPx, eyeLinePct: 0.6, groundLinePct: this.framing.groundPct };
      }
      // x tracks the mains' midpoint (art §7.3: dolly + x-track only)
      const m0 = this.creatures[0], m1 = this.creatures[1];
      const midX = m0?.hasFrame && m1?.hasFrame ? ((m0.geom.min[0] + m0.geom.max[0]) / 2 + (m1.geom.min[0] + m1.geom.max[0]) / 2) / 2 : 0;
      const f = this.framing.cam, a = this.arenaCam;
      a.eye[0] = f.eye[0] + midX + this.trackOffsetX; a.eye[1] = f.eye[1]; a.eye[2] = f.eye[2];
      a.target[0] = f.target[0] + midX + this.trackOffsetX; a.target[1] = f.target[1]; a.target[2] = f.target[2];
      a.fovYDeg = f.fovYDeg; a.near = f.near; a.far = f.far;
      this.camState = a;
      return;
    }
    this.camState = orbitCamera([0, 0.5, 0], 1.3, 24, 8, 0);
  }

  private readonly pr = { x: 0, y: 0, visible: false, depth: 0 };
  private screenRect(c: Creature): PanelRect {
    const g = c.geom;
    let x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
    for (let i = 0; i < 8; i++) {
      const p: Vec3 = [(i & 1) ? g.max[0] : g.min[0], (i & 2) ? g.max[1] : g.min[1], (i & 4) ? g.max[2] : g.min[2]];
      projectPoint(this.cam.viewProj, { w: this.vp.cssW, h: this.vp.cssH }, p, this.pr);
      x0 = Math.min(x0, this.pr.x); y0 = Math.min(y0, this.pr.y); x1 = Math.max(x1, this.pr.x); y1 = Math.max(y1, this.pr.y);
    }
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  project(world: Vec3): { x: number; y: number; visible: boolean } {
    const canvas={w:this.vp.cssW,h:this.vp.cssH};
    // Contact effects project before frame(). A freshly selected cinematic shot
    // must not inherit the preceding shot's screen-space cut direction.
    if(this.camOverride)cameraMatrices(this.projectCam,this.camOverride,canvas,this.rects().picture,this.feel.out.fovScale);
    const o = projectPoint(this.camOverride?this.projectCam.viewProj:this.cam.viewProj, canvas, world, { x: 0, y: 0, visible: false, depth: 0 });
    return { x: o.x, y: o.y, visible: o.visible };
  }

  createPortrait(): PortraitView {
    // P10 owns portraits (src/engine/portrait/); until then a no-op view keeps callers working.
    return { setDna: () => undefined, render: () => undefined, dispose: () => undefined };
  }

  setDebug(flags: Partial<{ wireframe: boolean; showBones: boolean; noPost: boolean; normals: boolean; contacts: boolean; smear: boolean }>): void {
    if (flags.noPost !== undefined) this.debugView = flags.noPost ? DEBUG_VIEW.nopost : DEBUG_VIEW.none;
    if (flags.normals !== undefined) this.debugView = flags.normals ? DEBUG_VIEW.normals : DEBUG_VIEW.none;
    this.requestFrame();
  }

  private destroyTargets(): void {
    const t = this.gpu?.targets;
    if (!t) return;
    t.msaaColor?.destroy(); t.resolved.destroy(); t.held.destroy(); t.depth.destroy();
    this.gpu!.targets = null;
    this.gpu!.groups.post = null;
  }

  private ensureTargets(st: GpuState): NonNullable<GpuState['targets']> {
    const w = this.size.sceneW, h = this.size.sceneH;
    if (st.targets && st.targets.w === w && st.targets.h === h) return st.targets;
    this.destroyTargets();
    const fmt = st.settings.sceneFormat, msaa = st.settings.msaa;
    const d = st.device;
    const msaaColor = msaa > 1 ? d.createTexture({ size: [w, h], format: fmt, sampleCount: msaa, usage: GPUTextureUsage.RENDER_ATTACHMENT, label: 'sceneMsaa' }) : null;
    const resolved = d.createTexture({ size: [w, h], format: fmt, usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_SRC, label: 'sceneResolved' });
    const depth = d.createTexture({ size: [w, h], format: DEPTH_FORMAT, sampleCount: msaa, usage: GPUTextureUsage.RENDER_ATTACHMENT, label: 'depth' });
    const held = d.createTexture({ size: [w, h], format: fmt, usage: GPUTextureUsage.COPY_DST | GPUTextureUsage.TEXTURE_BINDING, label: 'heldBands' });
    st.targets = { w, h, msaaColor, resolved, held, depth };
    st.groups.post = st.root.createBindGroup(postLayout, { scene: resolved.createView(), held: held.createView(), samp: st.sampler, post: st.buf.post });
    const bpp = fmt === 'rgba16float' ? 8 : 4;
    const page=st.illustration?.texture;
    this.stats.textureMB = ((w * h * bpp * (msaa + 1) + w * h * 4 * msaa+(page?page.width*page.height*4:0)) / (1024 * 1024));
    return st.targets;
  }

  frame(fpsCap:number|null=null): void {
    const st = this.gpu;
    if (!st || this.destroyed) return;
    const t0 = performance.now();
    this.requested = false;
    if (this.lastFrameAt > 0) {
      const dt = t0 - this.lastFrameAt;
      this.dts[this.dtHead] = dt; this.dtHead = (this.dtHead + 1) % this.dts.length;
      if (this.autoTier) { const nt = this.autoTier.push(dt,fpsCap); if (nt) void this.setQuality(nt); }
    }
    this.lastFrameAt = t0;
    const tg = this.ensureTargets(st);
    const S = st.settings;
    const pres = this.clocks.presentMs;
    const shaderT = this.freezeT ?? pres / 1000;
    const feel = this.feel.evaluate(pres);
    const pxPerCss = this.size.pxPerCss;
    const sceneScale = S.renderScale;

    // camera over the picture interior (scene px)
    this.updateCamera();
    const { panel, picture, band } = this.rects();
    const sc = pxPerCss * sceneScale;
    cameraMatrices(this.cam, this.camState, { w: this.size.sceneW, h: this.size.sceneH }, { x: picture.x * sc, y: picture.y * sc, w: picture.w * sc, h: picture.h * sc }, feel.fovScale);
    const F = this.frameHost;
    F.set(this.cam.viewProj, FR.viewProj); F.set(this.cam.view, FR.view); F.set(this.cam.proj, FR.proj);
    F[FR.eye] = this.cam.eye[0]; F[FR.eye + 1] = this.cam.eye[1]; F[FR.eye + 2] = this.cam.eye[2]; F[FR.eye + 3] = 1;
    F[FR.timing] = shaderT; F[FR.timing + 1] = tg.w / tg.h; F[FR.timing + 2] = this.camState.near; F[FR.timing + 3] = this.camState.far;
    F[FR.viewport] = tg.w; F[FR.viewport + 1] = tg.h; F[FR.viewport + 2] = sc; F[FR.viewport + 3] = sceneScale;

    // scene light: timing, viewport, panel, quality, contacts
    const L = this.lightHost, LU = this.lightU32;
    const lp = LIGHTING[this.lighting];
    L[SL.hatch + 1] = lp.hatchPx * sc; L[SL.hatch + 2] = S.hatchLayers; L[SL.hatch + 3] = S.furDensity;
    L[SL.timing] = shaderT; L[SL.timing + 1] = 1; L[SL.timing + 2] = BACKDROP_ID[this.backdrop]; L[SL.timing + 3] = 0;
    if(this.scene.kind==='expedition'){
      L[SL.timing+2]=7;L[SL.expedition]=this.scene.seed;L[SL.expedition+1]=this.scene.biome;
      L[SL.expedition+2]=this.scene.weather;L[SL.expedition+3]=this.scene.travel;
    }
    L[SL.viewportPx] = tg.w; L[SL.viewportPx + 1] = tg.h; L[SL.viewportPx + 2] = sc; L[SL.viewportPx + 3] = tierIndex(this.quality);
    L[SL.panelRectPx] = picture.x * sc; L[SL.panelRectPx + 1] = picture.y * sc; L[SL.panelRectPx + 2] = picture.w * sc; L[SL.panelRectPx + 3] = picture.h * sc;
    let nc = 0;
    let smearSlots = 0;
    let silhouette = 0;
    for (const c of this.creatures) {
      if (!c || !c.visible) continue;
      for (let k = 0; k < 2; k++) {
        const r = c.contacts[k * 4 + 3];
        if (r > 0 && nc < MAX_CONTACTS) {
          L[SL.contact + nc * 4] = c.contacts[k * 4]; L[SL.contact + nc * 4 + 1] = c.contacts[k * 4 + 1];
          L[SL.contact + nc * 4 + 2] = c.contacts[k * 4 + 2]; L[SL.contact + nc * 4 + 3] = r;
          nc++;
        }
      }
      if (this.smearHost[c.slot * 4] > 0) smearSlots++;
      // silhouette width from the projected height (art §4.3: clamp(0.0095 × H_px, 1.5, 4.0) CSS px)
      const hPx = this.projectedHeightPx(c);
      const sil = Math.min(4, Math.max(1.5, 0.0095 * hPx)) * c.outline * lp.hullK;
      if (Math.abs(c.lookF[LOOK.line] - sil) > 1e-4) { c.lookF[LOOK.line] = sil; this.looksDirty = true; }
      silhouette = Math.max(silhouette, sil);
    }
    for(const actor of this.atelierActors) {
      if(!actor.visible||!actor.frame||nc>=MAX_CONTACTS)continue;
      const frame=actor.frame;
      const contacts=frame.groundContacts??[[frame.model[12],frame.model[14],Math.max(0,frame.bounds.minY),Math.min(.24,frame.bounds.radius*.55)]];
      for(const contact of contacts){if(nc>=MAX_CONTACTS)break;for(let k=0;k<4;k++)L[SL.contact+nc*4+k]=contact[k];nc++;}
    }
    LU[SL.qualityFlags] = tierIndex(this.quality);
    LU[SL.qualityFlags + 1] = nc;
    LU[SL.qualityFlags + 2] = S.bits;
    LU[SL.qualityFlags + 3] = this.debugView;
    this.extra.silhouettePx = silhouette;
    this.stats.contactsUsed = nc;
    this.stats.smearSlots = smearSlots;

    // props
    if (this.propsDirty) {
      for (const p of this.props) {
        if (!p) continue;
        this.propsHost.set(p.matrix, p.index * 16);
        this.propInfoHost[p.index * 4] = p.owner ? p.owner.slot : 0;
        this.propInfoHost[p.index * 4 + 1] = p.det < 0 ? 1 : 0;
      }
    }

    // composite uniforms (canvas device px)
    const P = this.postHost;
    const kindId = this.panelSpec ? FRAME_ID[this.panelSpec.frame] : FRAME_ID.none;
    P[PO.panel] = panel.x * pxPerCss; P[PO.panel + 1] = panel.y * pxPerCss; P[PO.panel + 2] = panel.w * pxPerCss; P[PO.panel + 3] = panel.h * pxPerCss;
    P[PO.picture] = picture.x * pxPerCss; P[PO.picture + 1] = picture.y * pxPerCss; P[PO.picture + 2] = picture.w * pxPerCss; P[PO.picture + 3] = picture.h * pxPerCss;
    P[PO.frame] = kindId; P[PO.frame + 1] = this.panelSpec?.archK ?? 0.35; P[PO.frame + 2] = band * pxPerCss; P[PO.frame + 3] = this.bandScale();
    P[PO.shake] = feel.shakePx[0] * pxPerCss; P[PO.shake + 1] = feel.shakePx[1] * pxPerCss; P[PO.shake + 2] = (feel.shakeRotDeg * Math.PI) / 180; P[PO.shake + 3] = 0;
    P[PO.fx] = shaderT; P[PO.fx + 1] = this.grade === 'neutral' ? 0.03 : S.grain; P[PO.fx + 2] = this.burn; P[PO.fx + 3] = 1;
    P[PO.impact] = feel.impactFrame; P[PO.impact + 1] = 1; P[PO.impact + 2] = feel.vignettePulse; P[PO.impact + 3] = this.feel.pulseVermilion;
    const gr = GRADES[this.grade];
    P[PO.grade] = gr[0]; P[PO.grade + 1] = gr[1]; P[PO.grade + 2] = gr[2]; P[PO.grade + 3] = gr[3];
    P[PO.view] = this.debugView; P[PO.view + 1] = pxPerCss; P[PO.view + 2] = sceneScale; P[PO.view + 3] = S.inkBleedTaps;
    P[PO.canvas] = this.size.canvasW; P[PO.canvas + 1] = this.size.canvasH; P[PO.canvas + 2] = tg.w; P[PO.canvas + 3] = tg.h;
    if (feel.impactFrame !== 0) this.extra.impactFramesShown++;
    this.extra.shakePx = Math.hypot(feel.shakePx[0], feel.shakePx[1]);
    this.extra.zoomPunch = 1 - feel.fovScale;
    this.extra.flashMsLast1s = this.feel.flashMsLast1s(pres);
    this.stats.flashFramesLast1s = feel.flashFramesLast1s;

    // looks mirror
    if (this.looksDirty) for (const c of this.creatures) if (c) this.looksHost.set(c.lookF, c.slot * LOOK_FLOATS);

    // particles
    let nParticles = 0;
    if (this.pool.count > 0) {
      const need = Math.min(this.pool.count, S.particleCap) * PARTICLE_FLOATS;
      if (this.particleHost.length < need) this.particleHost = new Float32Array(TIER_SETTINGS.ultra.particleCap * PARTICLE_FLOATS);
      nParticles = this.pool.writeInstances(this.particleHost, this.clocks.vfxMs, [this.cam.view[0] >= 0 ? 1 : -1, 0]);
      nParticles = Math.min(nParticles, S.particleCap);
    }
    this.stats.particles = nParticles;
    const nTrailVertices=this.reducedMotion?0:this.trails.write(this.trailHost,pres);

    // triptych bands: rects in scene px, a Frame per live band, composite rects in canvas px
    let subRects: Array<{ x: number; y: number; w: number; h: number }> | null = null;
    let dissolve = 1;
    if (this.sub.length && this.subEndAt !== null) {
      dissolve = Math.max(0, 1 - (pres - this.subEndAt) / Math.max(1, this.subDissolveMs));
      if (dissolve <= 0) { this.sub = []; this.subEndAt = null; this.stats.subPanels = 0; }
    }
    if (this.sub.length) {
      subRects = [];
      let frozenBits = 0;
      for (let i = 0; i < this.sub.length; i++) {
        const b = this.sub[i];
        const rx = picture.x + b.rect01[0] * picture.w, ry = picture.y + b.rect01[1] * picture.h;
        const rw = b.rect01[2] * picture.w, rh = b.rect01[3] * picture.h;
        const sr = { x: Math.max(0, Math.round(rx * sc)), y: Math.max(0, Math.round(ry * sc)), w: Math.max(1, Math.round(rw * sc)), h: Math.max(1, Math.round(rh * sc)) };
        sr.w = Math.min(sr.w, tg.w - sr.x); sr.h = Math.min(sr.h, tg.h - sr.y);
        subRects.push(sr);
        const cm = this.subCams[i];
        cameraMatrices(cm, b.cam ?? this.camState, { w: sr.w, h: sr.h }, { x: 0, y: 0, w: sr.w, h: sr.h }, feel.fovScale);
        const H = this.subHosts[i];
        H.set(F);
        H.set(cm.viewProj, FR.viewProj); H.set(cm.view, FR.view); H.set(cm.proj, FR.proj);
        H[FR.eye] = cm.eye[0]; H[FR.eye + 1] = cm.eye[1]; H[FR.eye + 2] = cm.eye[2];
        H[FR.viewport] = sr.w; H[FR.viewport + 1] = sr.h;
        H[FR.subPanel] = sr.x; H[FR.subPanel + 1] = sr.y; H[FR.subPanel + 2] = sr.w; H[FR.subPanel + 3] = sr.h;
        P[PO.sub + i * 4] = rx * pxPerCss; P[PO.sub + i * 4 + 1] = ry * pxPerCss; P[PO.sub + i * 4 + 2] = rw * pxPerCss; P[PO.sub + i * 4 + 3] = rh * pxPerCss;
        if (b.frozen && !b.copyPending) frozenBits |= 1 << i;
      }
      P[PO.subFlags] = this.sub.length; P[PO.subFlags + 1] = frozenBits;
      P[PO.subFlags + 2] = Math.min(1, (pres - this.subStartMs) / Math.max(1, this.subDividerMs)); P[PO.subFlags + 3] = dissolve;
    } else {
      P[PO.subFlags] = 0; P[PO.subFlags + 1] = 0; P[PO.subFlags + 2] = 0; P[PO.subFlags + 3] = 0;
    }

    // ── uploads ──
    const q = st.device.queue;
    let up = 0;
    q.writeBuffer(st.buf.frame, 0, F); up += F.byteLength;
    if (subRects) for (let i = 0; i < subRects.length; i++) { q.writeBuffer(st.buf.frames[i], 0, this.subHosts[i]); up += F.byteLength; }
    q.writeBuffer(st.buf.light, 0, L); up += L.byteLength;
    if (this.matsDirty) { q.writeBuffer(st.buf.mats, 0, this.matsHost); up += this.matsHost.byteLength; this.matsDirty = false; }
    if (this.bonesHi >= this.bonesLo) {
      const n = this.bonesHi - this.bonesLo + 1;
      q.writeBuffer(st.buf.bones, this.bonesLo * BONE_FLOATS * 4, this.bonesHost, this.bonesLo * BONE_FLOATS, n * BONE_FLOATS);
      up += n * BONE_FLOATS * 4;
      this.bonesLo = MAX_SLOTS; this.bonesHi = -1;
    }
    for (let s = 0; s < MAX_SLOTS; s++) if (this.lagDirty[s]) {
      q.writeBuffer(st.buf.lag, s * BONE_FLOATS * 4, this.lagHost, s * BONE_FLOATS, BONE_FLOATS);
      up += BONE_FLOATS * 4; this.lagDirty[s] = 0;
    }
    q.writeBuffer(st.buf.smear, 0, this.smearHost); up += this.smearHost.byteLength;
    if (this.pivotDirty) { q.writeBuffer(st.buf.pivot, 0, this.pivotHost); up += this.pivotHost.byteLength; this.pivotDirty = false; }
    if (this.looksDirty) { q.writeBuffer(st.buf.looks, 0, this.looksHost); up += this.looksHost.byteLength; this.looksDirty = false; }
    if (this.propsDirty) {
      q.writeBuffer(st.buf.props, 0, this.propsHost); q.writeBuffer(st.buf.propInfo, 0, this.propInfoHost);
      up += this.propsHost.byteLength + this.propInfoHost.byteLength; this.propsDirty = false;
    }
    q.writeBuffer(st.buf.post, 0, P); up += P.byteLength;
    if (nParticles > 0) { q.writeBuffer(st.buf.particles, 0, this.particleHost, 0, nParticles * PARTICLE_FLOATS); up += nParticles * PARTICLE_FLOATS * 4; }
    if(nTrailVertices){q.writeBuffer(st.buf.trails,0,this.trailHost,0,nTrailVertices*TRAIL_FLOATS);up+=nTrailVertices*TRAIL_FLOATS*4;}
    this.stats.uploadBytes = up;

    // ── encode ──
    const enc = st.device.createCommandEncoder({ label: 'frame' });
    const msaa = S.msaa > 1;
    const colorView = msaa ? tg.msaaColor!.createView() : tg.resolved.createView();
    const pass = enc.beginRenderPass({
      label: 'scene',
      colorAttachments: [msaa
        ? { view: colorView, resolveTarget: tg.resolved.createView(), loadOp: 'clear', storeOp: 'discard', clearValue: [0, 0, 0, 0] }
        : { view: colorView, loadOp: 'clear', storeOp: 'store', clearValue: [0, 0, 0, 0] }],
      depthStencilAttachment: { view: tg.depth.createView(), depthClearValue: 1, depthLoadOp: 'clear', depthStoreOp: 'discard' },
    });
    const pp = st.pipes;
    const G = st.groups;
    let draws = 0, tris = 0, mirroredDraws = 0;
    const show3d = this.scene.kind !== 'none';
    for(const actor of this.atelierActors)if(actor.visible&&actor.frame&&actor.gpu)actor.gpu.upload(actor.frame,this.cam.viewProj,this.cam.eye,tg.w,tg.h,sc,this.scene.kind==='expedition');
    const drawScene = (FG: TgpuBindGroup): void => {
      for(const actor of this.atelierActors)if(actor.visible&&actor.frame&&actor.gpu){const count=actor.gpu.draw(pass);draws+=count.draws;tris+=count.triangles;}
      // 1. figures
      for (const c of this.creatures) {
        if (!c || !c.visible) continue;
        const m = st.meshes.get(c.meshKey);
        if (!m) continue;
        const variant = c.mirrored ? 'cw' : 'ccw';
        if (c.mirrored) mirroredDraws++;
        pp.figure[variant].with(FG).with(G.skin).with(creatureVertexLayout, m.vb).with(pass).draw(m.vertexCount, 1, 0, c.slot);
        draws++; tris += m.vertexCount / 3;
      }
      // 1b. props
      for (const p of this.props) {
        if (!p || !p.visible) continue;
        const m = st.meshes.get(p.meshKey);
        if (!m) continue;
        const variant = p.det < 0 ? 'cw' : 'ccw';
        pp.prop[variant].with(FG).with(G.skin).with(G.prop).with(creatureVertexLayout, m.vb).with(pass).draw(m.vertexCount, 1, 0, p.index);
        draws++; tris += m.vertexCount / 3;
      }
      // 2. floor (arena / stage)
      if (this.scene.kind === 'arena' || this.scene.kind === 'stage') {
        pp.floor.with(FG).with(pass).draw(12);
        draws++; tris += 4;
      }
      // 3. hulls (ink) and keylines on dark grounds
      for (const c of this.creatures) {
        if (!c || !c.visible) continue;
        const m = st.meshes.get(c.meshKey);
        if (!m || !m.hullVb || !m.hullIb) continue;
        const variant = c.mirrored ? 'cw' : 'ccw';
        pp.hull[variant].with(FG).with(G.skin).with(hullVertexLayout, m.hullVb).withIndexBuffer(m.hullIb, 'uint16').with(pass).drawIndexed(m.hullIndexCount, 1, 0, 0, c.slot);
        draws++; tris += m.hullIndexCount / 3;
        if (c.keyline) {
          pp.keyline[variant].with(FG).with(G.skin).with(hullVertexLayout, m.hullVb).withIndexBuffer(m.hullIb, 'uint16').with(pass).drawIndexed(m.hullIndexCount, 1, 0, 0, c.slot);
          draws++; tris += m.hullIndexCount / 3;
        }
      }
      for (const p of this.props) {
        if (!p || !p.visible) continue;
        const m = st.meshes.get(p.meshKey);
        if (!m || !m.hullVb || !m.hullIb) continue;
        const variant = p.det < 0 ? 'cw' : 'ccw';
        pp.propHull[variant].with(FG).with(G.skin).with(G.prop).with(hullVertexLayout, m.hullVb).withIndexBuffer(m.hullIb, 'uint16').with(pass).drawIndexed(m.hullIndexCount, 1, 0, 0, p.index);
        draws++; tris += m.hullIndexCount / 3;
      }
      // 4. background at z = 1 (early-Z skips covered pixels)
      if(this.scene.kind==='expedition'&&st.illustration)pp.illustration.with(FG).with(st.illustration.group).with(pass).draw(3);
      else pp.sky.with(FG).with(pass).draw(3);
      draws++; tris += 1;
      // 6. particles
      if(nTrailVertices){pp.trails.with(FG).with(trailLayout,st.buf.trails).with(pass).draw(nTrailVertices);draws++;tris+=nTrailVertices/3;}
      if (nParticles > 0) {
        pp.particles.with(FG).with(particleLayout, st.buf.particles).with(pass).draw(6, nParticles);
        draws++; tris += nParticles * 2;
      }
    };
    let liveBands = 0;
    if (show3d && subRects) {
      // triptych: each live band gets its own viewport, scissor and camera inside the one scene pass (cheatsheet R5)
      for (let i = 0; i < this.sub.length; i++) {
        const b = this.sub[i];
        if (b.frozen && !b.copyPending) continue;
        const r = subRects[i];
        pass.setViewport(r.x, r.y, r.w, r.h, 0, 1);
        pass.setScissorRect(r.x, r.y, r.w, r.h);
        drawScene(G.frames[i + 1]);
        liveBands++;
      }
    } else if (show3d) {
      drawScene(G.frame);
    }
    this.extra.liveBands = liveBands;
    pass.end();
    if (subRects) {
      for (let i = 0; i < this.sub.length; i++) {
        const b = this.sub[i];
        if (!b.copyPending) continue;
        const r = subRects[i];
        enc.copyTextureToTexture({ texture: tg.resolved, origin: [r.x, r.y] }, { texture: tg.held, origin: [r.x, r.y] }, [r.w, r.h]);
        b.copyPending = false;
      }
    }

    // composite → canvas
    const canvasTex = st.context.getCurrentTexture();
    const post = enc.beginRenderPass({
      label: 'composite',
      colorAttachments: [{ view: canvasTex.createView(), loadOp: 'clear', storeOp: 'store', clearValue: [0, 0, 0, 0] }],
    });
    if (show3d && G.post) {
      pp.composite.with(G.post).with(post).draw(3);
      draws++;
    }
    post.end();

    // debug readbacks (samplePixel / capture): copy the composited canvas texture
    let readback: { buf: GPUBuffer; w: number; h: number; bpr: number; x: number; y: number; reads: PendingRead[]; caps: Array<(url: string) => void> } | null = null;
    if (this.pendingReads.length || this.pendingCaptures.length) {
      const full = this.pendingCaptures.length > 0;
      const w = full ? canvasTex.width : 1, h = full ? canvasTex.height : 1;
      const bpr = Math.ceil((w * 4) / 256) * 256;
      const x = 0, y = 0;
      const rb = st.device.createBuffer({ size: bpr * h, usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ });
      const reads = this.pendingReads.splice(0), caps = this.pendingCaptures.splice(0);
      if (full) enc.copyTextureToBuffer({ texture: canvasTex, origin: [0, 0] }, { buffer: rb, bytesPerRow: bpr }, [w, h]);
      readback = { buf: rb, w, h, bpr, x, y, reads, caps };
      if (!full && reads.length) {
        const r0 = reads[0];
        const ox = Math.min(canvasTex.width - 1, Math.max(0, Math.round(r0.x * pxPerCss)));
        const oy = Math.min(canvasTex.height - 1, Math.max(0, Math.round(r0.y * pxPerCss)));
        enc.copyTextureToBuffer({ texture: canvasTex, origin: [ox, oy] }, { buffer: rb, bytesPerRow: bpr }, [1, 1]);
        readback.reads = [r0]; this.pendingReads.push(...reads.slice(1));
      }
    }
    st.device.queue.submit([enc.finish()]);
    if (readback) void this.finishReadback(readback, st.canvasFormat, pxPerCss);

    this.stats.drawCalls = draws;
    this.stats.triangles = Math.round(tris);
    this.stats.mirroredDraws = mirroredDraws;
    let fb = 0;
    const fbSlots: number[] = [];
    for (const c of this.creatures) if (c && c.lookF[LOOK.fx + 3] > 0.5) { fb++; fbSlots.push(c.slot); }
    this.stats.frameBreakCount = fb;
    this.stats.frameBreakSlots = fbSlots;
    this.stats.cpuMs = performance.now() - t0;
    this.extra.frames++;
    this.updateFps();
  }

  private async finishReadback(rb: { buf: GPUBuffer; w: number; h: number; bpr: number; reads: Array<{ x: number; y: number; resolve: (v: [number, number, number]) => void }>; caps: Array<(u: string) => void> }, fmt: GPUTextureFormat, pxPerCss: number): Promise<void> {
    await rb.buf.mapAsync(GPUMapMode.READ);
    const data = new Uint8Array(rb.buf.getMappedRange().slice(0));
    rb.buf.unmap(); rb.buf.destroy();
    const bgra = fmt === 'bgra8unorm';
    const px = (x: number, y: number): [number, number, number, number] => {
      const o = y * rb.bpr + x * 4;
      return bgra ? [data[o + 2], data[o + 1], data[o], data[o + 3]] : [data[o], data[o + 1], data[o + 2], data[o + 3]];
    };
    for (const r of rb.reads) {
      const v = rb.w === 1 ? px(0, 0) : px(Math.round(r.x * pxPerCss), Math.round(r.y * pxPerCss));
      (r.resolve as (v: number[]) => void)(v);
    }
    if (rb.caps.length) {
      const img = new ImageData(rb.w, rb.h);
      for (let y = 0; y < rb.h; y++) for (let x = 0; x < rb.w; x++) {
        const v = px(x, y); const o = (y * rb.w + x) * 4;
        img.data[o] = v[0]; img.data[o + 1] = v[1]; img.data[o + 2] = v[2]; img.data[o + 3] = v[3];
      }
      const c = document.createElement('canvas');
      c.width = rb.w; c.height = rb.h;
      c.getContext('2d')!.putImageData(img, 0, 0);
      const url = c.toDataURL('image/png');
      for (const f of rb.caps) f(url);
    }
  }

  private projectedHeightPx(c: Creature): number {
    const g = c.geom;
    const a = projectPoint(this.cam.viewProj, { w: this.vp.cssW, h: this.vp.cssH }, [g.parts.full[0], 0, g.parts.full[2]], this.pr).y;
    const b = projectPoint(this.cam.viewProj, { w: this.vp.cssW, h: this.vp.cssH }, [g.parts.full[0], 1, g.parts.full[2]], this.pr).y;
    return Math.abs(a - b);
  }

  private updateFps(): void {
    let n = 0, sum = 0;
    for (let i = 0; i < this.dts.length; i++) if (this.dts[i] > 0) { n++; sum += this.dts[i]; }
    if (n > 0) this.stats.fps = 1000 / (sum / n);
  }

  /** p50 / p90 of recent frame deltas. */
  frameTimes(): { p50: number; p90: number } {
    const a = Array.from(this.dts).filter((v) => v > 0).sort((x, y) => x - y);
    if (!a.length) return { p50: 0, p90: 0 };
    return { p50: a[Math.floor(a.length * 0.5)], p90: a[Math.floor(a.length * 0.9)] };
  }

  destroy(): void {
    this.destroyed = true;
    this.illustrationSource=null;this.gpu?.illustration?.texture.destroy();
    for(const actor of this.atelierActors){actor.disposed=true;actor.gpu?.dispose();}this.atelierActors.clear();
    this.gpu?.atelier?.dispose();
    this.destroyTargets();
    this.gpu?.device.destroy();
    this.gpu = null;
  }
}

/** Creates and initialises the renderer (throws WebGpuUnavailableError). */
export async function createEngineRenderer(opts: RendererOptions): Promise<EngineRenderer> {
  const r = new EngineRenderer(opts);
  await r.init();
  return r;
}
